/**
 * Signal-Konzept Schritt 2 (07.08.2026) — Lebenszyklus der Zonen-Signale.
 *
 * Ersetzt den score-gesteuerten Phantom-Pfad für den antizyklischen Kernstil
 * durch einen, der an `computeSetupState` hängt (§7.8 im Signal-Konzept):
 * Auslöser = `zone_offen`, Ausgang = fester Stop OHNE Ziel, Horizont 26 Wochen.
 *
 * WARUM EINE EIGENE TABELLE UND NICHT `v5_phantom_plans`:
 * Der bestehende Outcome-Cron lebt außerhalb dieses Repos
 * (`/opt/swingtrader-api/src/netlify-handlers/track-phantom-outcomes.ts`,
 * siehe docs/W68/phantom-outcomes-flow.md) und hat einen bekannten, hier nicht
 * behebbaren Fehler: Ist `tp_price = null` ("kein Ziel"), bleibt ein Plan bis
 * zum 60-Tage-Expiry hängen — das widerspricht direkt dem 26-Wochen-Horizont
 * aus §7.4. Diese Tabelle wird komplett selbst verwaltet, hier im Repo, ohne
 * Abhängigkeit vom externen Tracker: Erzeugen UND Auflösen laufen im selben
 * Server-Cron (server-score-cron.ts), mit denselben Bars, die dort ohnehin
 * schon geladen sind.
 *
 * Reine Funktionen — keine DB-Zugriffe hier, die sitzen im Aufrufer.
 */

import type { SetupState } from './setup-state';
import { STOP_ATR14_FAKTOR } from './setup-state';
import type { OhlcvBar } from '@/lib/market-data/types';

/** 26 Wochen — siehe §7.4: kürzer unterschätzt das Setup um Faktor 2 bis 4. */
export const ZONE_HALTE_BARS = 130;

export type ZoneSignalStatus = 'offen' | 'ausgestoppt' | 'zeitausstieg';

export interface ZoneSignalRow {
  id: number;
  symbol: string;
  ausloeser: string;
  entry_price: number;
  stop_price: number;
  generated_date: string;
  status: ZoneSignalStatus;
  /** Tag des Teilverkaufs bei +2R. NULL = noch nicht erreicht. */
  teil_raus_at?: string | null;
}

/**
 * Zusatzdaten, die beim Signal MITGESCHRIEBEN, aber NICHT für die Auswahl
 * verwendet werden (Audit 08.08.2026, Punkt 4).
 *
 * Hintergrund: Nur `v5_ohlcv_history` hat echte Historie (~20 Jahre). VIX
 * reicht 6 Monate zurück, Short-Interest und Sentiment gar nicht. Deshalb
 * lässt sich heute KEINE nicht-preisbasierte Signalidee ehrlich prüfen —
 * jeder Rücktest wäre Look-ahead.
 *
 * Diese Felder ändern also bewusst nichts an der Entscheidung. Sie sind eine
 * Wette auf die Zukunft: Bei ~20 Signalen im Jahr liegen in 24 Monaten rund
 * 40 Datenpunkte vor — genug für eine erste ehrliche Frage „hätte einer
 * dieser Faktoren geholfen?". Ohne heutiges Mitschreiben steht man in zwei
 * Jahren wieder genau hier.
 *
 * WICHTIG für später: Ein Feld aus diesem Block darf erst dann in die
 * Auswahl wandern, wenn es die fünf Aufnahme-Kriterien aus §5b erfüllt —
 * insbesondere den Out-of-Sample-Test.
 */
export interface ZoneSignalKontext {
  /** FINRA-Short-Volume in Prozent, falls vorhanden. */
  short_interest_pct: number | null;
  /** Handelstage bis zu den nächsten Quartalszahlen. */
  tage_bis_earnings: number | null;
  /** VIX-Schlusskurs am Signaltag. */
  vix: number | null;
  /** Anteil der Watchlist über der eigenen MA200, in Prozent. */
  marktbreite_pct: number | null;
  /** Abstand des SPY zu seinem 52-Wochen-Hoch, in Prozent. */
  spy_dd52_pct: number | null;
}

export interface ZoneSignalInsert {
  symbol: string;
  ausloeser: string;
  entry_price: number;
  stop_price: number;
  generated_date: string;
  kennzahlen: SetupState['kennzahlen'] & { kontext?: ZoneSignalKontext };
}

/**
 * Soll ein neues Zonen-Signal erzeugt werden?
 *
 * Dedup wie beim alten Phantom-Gate: Solange für das Symbol noch ein offenes
 * Signal läuft, wird kein zweites erzeugt — ein mehrtägig offener Zustand
 * (die Zone bleibt oft mehrere Tage `zone_offen`) darf nicht zu mehreren
 * Einträgen für dieselbe Gelegenheit führen.
 */
/**
 * Darf aus diesem Zustand ein Kaufsignal entstehen?
 *
 * Seit 11.08.2026 liest diese Funktion `state.tradeable` statt selbst zu
 * urteilen (P0 aus dem V6-Audit). Vorher prüfte sie nur `zone_offen` — und
 * kannte damit weder Datenalter noch irgendeinen anderen Sperrgrund. Wer
 * einen neuen Blockiergrund einführt, ergänzt ihn in `computeSetupState`;
 * hier muss dann nichts angefasst werden, und genau das ist der Punkt:
 * EIN Ort entscheidet.
 */
export function shouldOpenZoneSignal(state: SetupState, hatOffenesSignal: boolean): boolean {
  return state.tradeable && !hatOffenesSignal;
}

export function buildZoneSignalInsert(
  symbol: string,
  state: SetupState,
  today: string,
  kontext?: ZoneSignalKontext
): ZoneSignalInsert | null {
  if (state.zustand !== 'zone_offen' || state.limit_bis === null || state.ungueltig_unter === null) {
    return null;
  }
  return {
    symbol: symbol.toUpperCase(),
    ausloeser: state.ausloeser ?? 'unbekannt',
    entry_price: state.limit_bis,
    stop_price: state.ungueltig_unter,
    generated_date: today,
    kennzahlen: kontext ? { ...state.kennzahlen, kontext } : state.kennzahlen,
  };
}

/** Bei welchem Vielfachen des Risikos die erste Hälfte verkauft wird. */
export const TEILVERKAUF_R = 2;
/**
 * Rücksetzer vom Lauf-Hoch, der die zweite Hälfte beendet. 15 % ist KEIN
 * gesuchter Parameter — es ist dieselbe Schwelle, mit der die Phasenmessung
 * eine Phase überhaupt definiert (scripts/messung-phasen.ts). Wenn 15 % Umkehr
 * eine Phase beendet, ist 15 % unter dem Lauf-Hoch der kausale Ausstieg dazu.
 */
export const REST_RUECKSETZER = 0.15;

export interface ZoneSignalOutcome {
  status: ZoneSignalStatus;
  outcome_price: number | null;
  outcome_r: number | null;
  outcome_pct: number | null;
  outcome_at: string | null;
}

/**
 * Löst ein offenes Zonen-Signal gegen die Bars SEIT dem Einstieg auf.
 *
 * Regel B1-B4 aus §7.4: fester Stop bei 4,5 × ATR14 (hier bereits als
 * `stop_price` im Signal hinterlegt), kein Kursziel, kein Nachziehen, 26
 * Wochen halten. Gap-Ehrlichkeit: Eröffnet ein Tag unter dem Stop, füllt der
 * Ausstieg dort — nicht am (günstigeren) Stop-Kurs.
 *
 * @param barsSeitEinstieg  Alle Bars ECHT NACH dem Einstiegstag, aufsteigend.
 *                          Leer/zu kurz → bleibt offen (noch nicht entschieden).
 */
export function resolveZoneSignal(
  signal: Pick<ZoneSignalRow, 'entry_price' | 'stop_price'>,
  barsSeitEinstieg: OhlcvBar[]
): ZoneSignalOutcome | null {
  const risiko = signal.entry_price - signal.stop_price;
  if (!(risiko > 0)) return null;

  for (let i = 0; i < barsSeitEinstieg.length; i++) {
    const b = barsSeitEinstieg[i]!;
    if (b.low <= signal.stop_price) {
      const fill = Math.min(signal.stop_price, b.open);
      return {
        status: 'ausgestoppt',
        outcome_price: fill,
        outcome_r: (fill - signal.entry_price) / risiko,
        outcome_pct: ((fill / signal.entry_price - 1) * 100),
        outcome_at: b.timestamp,
      };
    }
    if (i + 1 >= ZONE_HALTE_BARS) {
      return {
        status: 'zeitausstieg',
        outcome_price: b.close,
        outcome_r: (b.close - signal.entry_price) / risiko,
        outcome_pct: (b.close / signal.entry_price - 1) * 100,
        outcome_at: b.timestamp,
      };
    }
  }
  // Weder Stop noch 26 Wochen erreicht → bleibt offen.
  return null;
}

/** Referenz auf die Stop-Regel, damit Aufrufer nicht STOP_ATR14_FAKTOR selbst importieren müssen. */
export { STOP_ATR14_FAKTOR as ZONE_STOP_ATR14_FAKTOR };

/** Was mit der ersten Hälfte passiert ist. */
export interface TeilAusstieg {
  preis: number;
  datum: string;
}

export interface ZoneSignalGestaffelt {
  /** Erste Hälfte bei +2R verkauft — null, solange nicht erreicht. */
  teil: TeilAusstieg | null;
  /** Endergebnis über BEIDE Hälften. Null, solange die zweite noch läuft. */
  ergebnis: ZoneSignalOutcome | null;
}

/**
 * Gestaffelter Ausstieg — die einzige Änderung, die out-of-sample hielt.
 *
 * BEFUND (docs/konzepte/2026-08-08-phasen-pruefung.md, 51 Werte, 1971–2026):
 * Der bisherige Ausstieg fängt im Median nur 27,2 % der Bewegung ein, die bis
 * zum Phasenhoch möglich gewesen wäre. Sechs kausale Ausstiegsregeln wurden
 * gegeneinander gemessen — keine schlug die bisherige zuverlässig, weil eine
 * Phase per Definition mit einem Rücksetzer endet: Bei ~36 % Median-Phasen-
 * gewinn und ~15 % Bestätigungs-Rücksetzer gibt man rund die Hälfte allein
 * fürs Warten auf die Bestätigung ab. Das ist Arithmetik, keine Einstellung.
 *
 * Was hilft, ist keine andere Schwelle, sondern eine andere STRUKTUR:
 *
 *   Zeitraum              ganz (Median)   gestaffelt (Median)
 *   GESAMT                    +0,1 %           +6,6 %
 *   1990–2002 (echtes OOS)    −8,3 %           −8,5 %
 *   2003–2025 (in-sample)     +7,7 %          +14,0 %
 *
 * Beim Mittelwert hält es in JEDEM Zeitraum: −1,1 → +4,0 (1971–89),
 * +0,7 → +2,4 (OOS), 12,4 → 12,4 (in-sample). Gewinnerquote 50,1 → 52,4 %.
 *
 * Der eigentliche Gewinn ist aber nicht die Zahl: Man muss nicht mehr raten,
 * wann die Phase endet, weil nicht mehr alles an einem Zeitpunkt hängt.
 *
 * REGELN
 *   1. Erste Hälfte bei +2R (Tageshoch berührt die Marke).
 *   2. Danach wandert der Stop des Rests auf den Einstand — der Trade kann
 *      ab hier nicht mehr ins Minus drehen.
 *   3. Der Rest läuft, bis er 15 % unter das Lauf-Hoch fällt.
 *   4. KEINE Zeituhr. Die alte 130-Bar-Grenze stammte aus einem Konzeptpapier,
 *      nie aus einer Messung, und schnitt mitten in Phasen ab: gehalten wurde
 *      im Median 130 Tage, während die Phase noch 92 Tage lief.
 *
 * Gap-Ehrlichkeit wie bisher: Eröffnet ein Tag unter der Ausstiegslinie, wird
 * dort gefüllt, nicht am (günstigeren) Linienkurs.
 *
 * @param barsSeitEinstieg Alle Bars ECHT NACH dem Einstiegstag, aufsteigend.
 */
export function resolveZoneSignalGestaffelt(
  signal: Pick<ZoneSignalRow, 'entry_price' | 'stop_price'>,
  barsSeitEinstieg: OhlcvBar[]
): ZoneSignalGestaffelt {
  const risiko = signal.entry_price - signal.stop_price;
  if (!(risiko > 0)) return { teil: null, ergebnis: null };

  const ziel = signal.entry_price + TEILVERKAUF_R * risiko;
  let teil: TeilAusstieg | null = null;
  let hoch = signal.entry_price;

  for (const b of barsSeitEinstieg) {
    // Reihenfolge zählt: Der Stop wird VOR dem Ziel geprüft, weil aus einer
    // Tagesbar nicht hervorgeht, was zuerst kam. Die vorsichtige Annahme.
    const linie = teil
      ? Math.max(signal.entry_price, hoch * (1 - REST_RUECKSETZER))
      : signal.stop_price;

    if (b.low <= linie) {
      const fill = Math.min(linie, b.open);
      // Ergebnis über beide Hälften gewichten.
      const teilErtrag = teil ? 0.5 * (teil.preis - signal.entry_price) : 0;
      const restAnteil = teil ? 0.5 : 1;
      const gesamt = teilErtrag + restAnteil * (fill - signal.entry_price);
      return {
        teil,
        ergebnis: {
          status: teil ? 'zeitausstieg' : 'ausgestoppt',
          outcome_price: fill,
          outcome_r: gesamt / risiko,
          outcome_pct: (gesamt / signal.entry_price) * 100,
          outcome_at: b.timestamp,
        },
      };
    }

    if (!teil && b.high >= ziel) teil = { preis: ziel, datum: b.timestamp };
    if (b.close > hoch) hoch = b.close;
  }

  // Noch nicht entschieden — die zweite Hälfte läuft weiter.
  return { teil, ergebnis: null };
}
